/**
 * `search.yaml` — the four ways a person can correct their own search.
 *
 * Kept as CONTENT, beside `nav.yaml` and `variables.yaml`, in the customer's
 * own repository. Three reasons, all of which a database row would lose: the
 * tuning travels with the words it is tuning, it can be changed on a draft and
 * reviewed with everything else in that draft, and a customer who leaves takes
 * it with them. Search tuning is written BY the same people, ABOUT the same
 * pages, and reverted for the same reasons.
 *
 * The four are genuinely different acts, which is why they are four keys and
 * not one clever one:
 *
 *   synonyms  Words your readers use and your docs do not — SSO for SAML.
 *             Symmetric: any of a group finds pages using any other.
 *   aliases   One query means another, in one direction only. "pricing" →
 *             "plans" should not make every plans search also mean pricing.
 *   pinned    A query where you already know the right answer and want it
 *             first, whatever the ranking thinks.
 *   boost     A page that deserves more or less weight everywhere, not for
 *             one query.
 *
 * Nothing here can hide a page. Boost multiplies a score, so a page can sink;
 * it cannot vanish, because a search that silently omits a matching page is a
 * search nobody can debug — `noindex` is the honest way to leave results.
 */
import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import { keepMapComments } from './index.js';
export const EMPTY_TUNING = { synonyms: [], aliases: {}, pinned: {}, boost: {} };
/**
 * How far a boost may go.
 *
 * A range, not a free number. Ten times is already "this page always wins",
 * and a tenth is "this page is never seen"; beyond that a boost stops being
 * a weighting and becomes a way to break search from a content file, which is
 * the thing this format must not allow.
 */
const BOOST_MIN = 0.1;
const BOOST_MAX = 10;
const PAGE_PATH = /^pages\/[A-Za-z0-9._/-]+\.md$/;
export function parseSearchTuning(yaml) {
    const raw = yaml.trim() === '' ? {} : parseYaml(yaml);
    if (raw === null || raw === undefined)
        return { ...EMPTY_TUNING };
    if (typeof raw !== 'object' || Array.isArray(raw)) {
        throw new Error('search.yaml must be a mapping, with any of: synonyms, aliases, pinned, boost');
    }
    const map = raw;
    const known = ['synonyms', 'aliases', 'pinned', 'boost'];
    for (const key of Object.keys(map)) {
        if (!known.includes(key)) {
            throw new Error(`"${key}" is not a search setting — use one of: ${known.join(', ')}`);
        }
    }
    return {
        synonyms: readSynonyms(map['synonyms']),
        aliases: readAliases(map['aliases']),
        pinned: readPinned(map['pinned']),
        boost: readBoost(map['boost']),
    };
}
function readSynonyms(value) {
    if (value === undefined || value === null)
        return [];
    if (!Array.isArray(value)) {
        throw new Error('synonyms must be a list of groups, like `- [sso, saml, single sign-on]`');
    }
    return value.map((group, i) => {
        if (!Array.isArray(group)) {
            throw new Error(`synonym group ${i + 1} must be a list of words, like [sso, saml]`);
        }
        const words = group.map((word) => String(word).trim().toLowerCase()).filter((w) => w !== '');
        // A group of one is a word that means itself. Refused rather than ignored:
        // it is always a half-finished edit, and silence about it is how it stays
        // half-finished.
        if (words.length < 2) {
            throw new Error(`synonym group ${i + 1} needs at least two words to mean anything`);
        }
        return words;
    });
}
function readAliases(value) {
    if (value === undefined || value === null)
        return {};
    if (typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('aliases must be a mapping of typed query to real query, like `pricing: plans`');
    }
    const out = {};
    for (const [from, to] of Object.entries(value)) {
        const target = String(to ?? '').trim();
        if (target === '')
            throw new Error(`the alias "${from}" has nothing to point at`);
        if (target.toLowerCase() === from.trim().toLowerCase()) {
            throw new Error(`the alias "${from}" points at itself`);
        }
        out[from.trim().toLowerCase()] = target.toLowerCase();
    }
    return out;
}
function readPinned(value) {
    if (value === undefined || value === null)
        return {};
    if (typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('pinned must be a mapping of query to page paths, like `sso: [pages/sso.md]`');
    }
    const out = {};
    for (const [query, paths] of Object.entries(value)) {
        const list = Array.isArray(paths) ? paths : [paths];
        out[query.trim().toLowerCase()] = list.map((path) => {
            const page = String(path).trim();
            if (!PAGE_PATH.test(page)) {
                throw new Error(`"${page}" is not a page path — pin something like pages/sso.md`);
            }
            return page;
        });
    }
    return out;
}
function readBoost(value) {
    if (value === undefined || value === null)
        return {};
    if (typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('boost must be a mapping of page path to a number, like `pages/start.md: 2`');
    }
    const out = {};
    for (const [path, weight] of Object.entries(value)) {
        const page = path.trim();
        if (!PAGE_PATH.test(page)) {
            throw new Error(`"${page}" is not a page path — boost something like pages/start.md`);
        }
        const number = typeof weight === 'number' ? weight : Number(String(weight));
        if (!Number.isFinite(number))
            throw new Error(`the boost for "${page}" is not a number`);
        if (number < BOOST_MIN || number > BOOST_MAX) {
            throw new Error(`the boost for "${page}" is ${number}; keep it between ${BOOST_MIN} and ${BOOST_MAX} — ` +
                `beyond that it stops weighting a page and starts replacing the ranking`);
        }
        out[page] = number;
    }
    return out;
}
/**
 * Write the file back.
 *
 * Canonically, and therefore losing any comments a person put in it — the same
 * trade `variables.yaml` makes, and worth stating rather than discovering.
 * The alternative is editing YAML in place, which means a parser that
 * round-trips trivia and a whole class of bugs where a file comes back subtly
 * different from the one that went in.
 *
 * Empty sections are left OUT rather than written as empty maps. A file that
 * says `pinned: {}` invites somebody to fill it in and tells a reader the
 * product cares; a file that says nothing about pins is a file about the two
 * things this project actually does.
 */
export function serializeSearchTuning(tuning, previous) {
    const out = {};
    if (tuning.synonyms.length > 0)
        out['synonyms'] = tuning.synonyms;
    if (Object.keys(tuning.aliases).length > 0) {
        out['aliases'] = sortedKeys(tuning.aliases);
    }
    if (Object.keys(tuning.pinned).length > 0)
        out['pinned'] = sortedKeys(tuning.pinned);
    if (Object.keys(tuning.boost).length > 0)
        out['boost'] = sortedKeys(tuning.boost);
    if (Object.keys(out).length === 0)
        return '';
    const text = stringifyYaml(out, { lineWidth: 0 });
    /**
     * The notes in the file being replaced.
     *
     * The last of the files this product rewrites for somebody. Its notes are
     * the reason a rule is there — "# pinned for the launch, remove in October"
     * above one result — so they hang on nested keys rather than on the top
     * level, which is why this one came last.
     */
    return previous === undefined ? text : keepMapComments(text, previous);
}
/** Sorted, so two edits that add the same thing produce the same file. */
function sortedKeys(map) {
    const ordered = {};
    for (const key of Object.keys(map).sort())
        ordered[key] = map[key];
    return ordered;
}
//# sourceMappingURL=search-tuning.js.map